/**
 * Phone menu for the custom mega menu.
 *
 * On phones the theme's menu drawer normally lists the Shopify navigation menu. When the header uses the custom mega
 * menu (blocks/_mega-item.liquid), this rebuilds the drawer's list from those same menu items, so phone and desktop
 * always match and there is only one menu to edit:
 *   - each menu item with a dropdown becomes an accordion, with a "Shop all" button (label from the item's
 *     "Phone button label" setting) and its link lists as smaller accordions (the first one open);
 *   - a discount code or text block is shown full width;
 *   - promo cards, image links and collection tiles become a row you can swipe;
 *   - the perks bar is shown once, at the bottom of the drawer.
 * The desktop dropdowns stay in the page (hidden on phones), so the phone menu is built by copying from them.
 * Styles live in assets/mega-menu-mobile.css.
 */

const CHEVRON =
  '<svg class="mmm__chev" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ARROW =
  '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false"><path d="M4 10h11M11 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Copies an element without the theme editor's block markers, so the editor doesn't confuse the copy with the block.
 * Ids inside the copy get a suffix, and references to them (SVG gradients and clip paths, url(#id) / #id) follow, so
 * the copy never points at the original, which stays hidden in the desktop dropdown and would draw nothing.
 */
function copyOf(element) {
  const copy = /** @type {HTMLElement} */ (element.cloneNode(true));
  copy.removeAttribute('data-shopify-editor-block');
  copy.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));

  const ids = new Map([...copy.querySelectorAll('[id]')].map((node) => [node.id, `${node.id}-mmm`]));
  if (ids.size) {
    copy.querySelectorAll('[id]').forEach((node) => {
      node.id = ids.get(node.id) ?? node.id;
    });
    copy.querySelectorAll('*').forEach((node) => {
      for (const attr of [...node.attributes]) {
        if (!attr.value.includes('#')) continue;
        const value = attr.value.replace(/url\(#([^)]+)\)|^#(.+)$/g, (match, inUrl, whole) => {
          const id = inUrl ?? whole;
          if (!ids.has(id)) return match;
          return inUrl ? `url(#${ids.get(id)})` : `#${ids.get(id)}`;
        });
        if (value !== attr.value) node.setAttribute(attr.name, value);
      }
    });
  }
  return copy;
}

/** @param {Element} block a .mm-links block @param {boolean} open */
function buildLinkList(block, open) {
  const links = block.querySelectorAll(':scope > .mm-links__list > li');
  const list = document.createElement('ul');
  list.className = 'mmm__links';
  list.setAttribute('role', 'list');
  links.forEach((link) => list.append(copyOf(link)));

  const heading = block.querySelector(':scope > .mm-links__heading');
  if (!heading) return list;

  const details = document.createElement('details');
  details.className = 'mmm__sub';
  details.open = open;

  const summary = document.createElement('summary');
  const title = document.createElement('span');
  title.className = 'mm-links__heading';
  title.innerHTML = heading.innerHTML;
  summary.append(title);
  summary.insertAdjacentHTML('beforeend', CHEVRON);

  details.append(summary, list);
  return details;
}

/** @param {HTMLElement} item a desktop .mm-item */
function buildItem(item) {
  const topLink = item.querySelector('.menu-list__link');
  const label = topLink?.querySelector('.menu-list__link-title')?.textContent.trim() ?? '';
  const href = topLink?.getAttribute('href') || '#';
  const panel = item.querySelector('.menu-list__submenu .mm');

  const li = document.createElement('li');
  li.className = 'mmm__item';

  if (!panel) {
    const link = document.createElement('a');
    link.className = 'mmm__top';
    link.href = href;
    link.textContent = label;
    li.append(link);
    return li;
  }

  const details = document.createElement('details');
  details.className = 'mmm__group';

  const summary = document.createElement('summary');
  summary.className = 'mmm__top';
  const summaryText = document.createElement('span');
  summaryText.textContent = label;
  summary.append(summaryText);
  summary.insertAdjacentHTML('beforeend', CHEVRON);

  const body = document.createElement('div');
  body.className = 'mmm__body';

  const shopAll = document.createElement('a');
  shopAll.className = 'mmm__all';
  shopAll.href = href;
  shopAll.textContent = item.dataset.mobileCta || (/^shop/i.test(label) ? label : `Shop all ${label}`);
  shopAll.insertAdjacentHTML('beforeend', ARROW);
  body.append(shopAll);

  /** @type {Element[]} */
  const media = [];
  let firstList = true;

  for (const block of panel.children) {
    if (block.matches('.mm-links')) {
      body.append(buildLinkList(block, firstList));
      firstList = false;
    } else if (block.matches('.mm-stack')) {
      for (const child of block.children) {
        if (child.matches('.mm-coupon, .mm-text')) body.append(copyOf(child));
        else media.push(child);
      }
    } else if (block.matches('.mm-cols')) {
      media.push(...block.querySelectorAll('.mm-tile'));
    } else if (block.matches('.mm-promo, .mm-card, .mm-banner, .mm-product')) {
      media.push(block);
    } else if (block.matches('.mm-coupon, .mm-text')) {
      body.append(copyOf(block));
    }
    // The perks bar and the bottom link are left out here; the perks bar is shown once at the bottom of the drawer.
  }

  if (media.length) {
    const row = document.createElement('div');
    row.className = 'mmm__media';
    media.forEach((element) => row.append(copyOf(element)));
    body.append(row);
  }

  details.append(summary, body);
  li.append(details);
  return li;
}

function build() {
  const nav = document.querySelector('.header__drawer .menu-drawer__navigation');
  const items = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('header-menu .mm-item'));
  if (!nav || !items.length || nav.querySelector(':scope > .mmm')) return;

  const root = document.createElement('div');
  root.className = 'mmm';

  // Line up with the rest of the drawer: use the side padding of the theme's own list, which this replaces.
  const themeList = nav.querySelector(':scope > .menu-drawer__menu');
  if (themeList) root.style.paddingInline = getComputedStyle(themeList).paddingInlineStart;

  const list = document.createElement('ul');
  list.className = 'mmm__list';
  list.setAttribute('role', 'list');
  items.forEach((item) => list.append(buildItem(item)));
  root.append(list);

  const perks = document.querySelector('header-menu .mm-trust');
  if (perks) {
    const wrap = document.createElement('div');
    wrap.className = 'mmm__perks';
    wrap.append(copyOf(perks));
    root.append(wrap);
  }

  nav.append(root);
  nav.classList.add('mmm-active');
}

function rebuild() {
  document.querySelectorAll('.mmm').forEach((node) => node.remove());
  build();
}

build();

// The header can be re-rendered (page transitions, theme editor); build again whenever the drawer comes back without it.
let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    build();
  });
}).observe(document.getElementById('header-group') ?? document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', rebuild);
document.addEventListener('shopify:block:select', rebuild);